import type { Signal } from '../../../../shared/types';
import { FILTER_KEYS, activeFilters, filterOptions } from '../../logic/signalFilters';
import type { FilterKey, Filters } from '../../logic/signalFilters';
import { h, replaceChildren, span } from '../../ui/dom';
import { SIGNAL_FILTER_NAMES } from '../../ui/labels';

export interface FilterBar {
  el: HTMLElement;
  /** Repaints when the list or the ticks changed since the last time; a moved selection costs nothing. */
  update: () => void;
  dispose: () => void;
}

interface FilterSource {
  readonly all: readonly Signal[];
  readonly filters: Filters;
}

type Toggle = (key: FilterKey, value: string, on: boolean) => void;

const classes = (...names: (string | false)[]) => names.filter(Boolean).join(' ');

/** One filter's menu: a pill that opens a list of ticks, each with how many it would show. */
const filterMenu = (
  inbox: FilterSource,
  key: FilterKey,
  open: boolean,
  toggle: Toggle,
): HTMLDetailsElement => {
  const ticked = inbox.filters[key];
  return h(
    'details',
    { class: classes('fmenu', ticked.size > 0 && 'fmenu--active'), open, dataset: { fkey: key } },
    h(
      'summary',
      { class: 'fmenu__btn' },
      span('fmenu__name', SIGNAL_FILTER_NAMES[key]),
      ticked.size ? span('fmenu__n', String(ticked.size)) : null,
    ),
    h(
      'div',
      { class: 'fmenu__panel', role: 'group', 'aria-label': SIGNAL_FILTER_NAMES[key] },
      filterOptions(inbox.all, inbox.filters, key).map((o) =>
        h(
          'label',
          { class: classes('fmenu__opt', o.count === 0 && 'fmenu__opt--none') },
          h('input', {
            type: 'checkbox',
            checked: ticked.has(o.value),
            dataset: { fkey: key, fvalue: o.value },
            onChange: (e: Event) => toggle(key, o.value, (e.currentTarget as HTMLInputElement).checked),
          }),
          span('fmenu__label', o.label),
          span('fmenu__count', String(o.count)),
        ),
      ),
    ),
  );
};

/** A tag per tick, named with its filter ("City: Irvine" is not "Source: Irvine"), and Clear. */
const filterTags = (inbox: FilterSource, toggle: Toggle, clear: () => void): HTMLElement[] => {
  const active = activeFilters(inbox.filters);
  if (active.length === 0) return [];
  return [
    ...active.map((a) => {
      const label = `${SIGNAL_FILTER_NAMES[a.key]}: ${a.label}`;
      return h(
        'span',
        { class: 'ftag' },
        span('ftag__label', label),
        h(
          'button',
          {
            type: 'button',
            class: 'ftag__x',
            'aria-label': `Remove the filter ${label}`,
            onClick: () => toggle(a.key, a.value, false),
          },
          '×',
        ),
      );
    }),
    h('button', { type: 'button', class: 'ftags__clear', onClick: clear }, 'Clear'),
  ];
};

/** The filters above the desktop list (D66): a menu per filter and a tag per tick. A repaint keeps the
 *  open menu open, its scroll and the focus; after a tag or Clear the focus goes to the filter's menu. */
export const signalFilterBar = (inbox: FilterSource, onChange: () => void): FilterBar => {
  const menusEl = h('div', { class: 'fbar__menus' });
  const tagsEl = h('div', { class: 'ftags', hidden: true });
  const el = h('div', { class: 'fbar' }, menusEl, tagsEl);
  const menus = () => [...menusEl.querySelectorAll<HTMLDetailsElement>('.fmenu')];
  const openMenu = () => menus().find((d) => d.open);
  const summaryOf = (key: FilterKey | undefined) =>
    menus()
      .find((d) => d.dataset.fkey === key)
      ?.querySelector('summary') ?? null;
  let painted: { all: readonly Signal[]; ticks: string } | null = null;
  let focusMenu: FilterKey | null = null;

  const toggle: Toggle = (key, value, on) => {
    if (on) inbox.filters[key].add(value);
    else {
      inbox.filters[key].delete(value);
      if (!menusEl.contains(document.activeElement)) focusMenu = key;
    }
    onChange();
  };
  const clear = () => {
    for (const key of FILTER_KEYS) inbox.filters[key].clear();
    focusMenu = FILTER_KEYS[0];
    onChange();
  };

  /** What the focus was on in the menus, to put it back on the same thing after a repaint. */
  const focusedNow = () => {
    const active = document.activeElement as HTMLElement | null;
    if (!active || !menusEl.contains(active)) return null;
    return { menu: active.closest<HTMLElement>('.fmenu')?.dataset.fkey, value: active.dataset.fvalue };
  };

  const repaint = () => {
    const open = openMenu();
    const scroll = open?.querySelector('.fmenu__panel')?.scrollTop ?? 0;
    const focused = focusedNow();
    replaceChildren(
      menusEl,
      FILTER_KEYS.map((key) => filterMenu(inbox, key, key === open?.dataset.fkey, toggle)),
    );
    const panel = openMenu()?.querySelector('.fmenu__panel');
    if (panel) panel.scrollTop = scroll;
    const tick =
      focused?.value === undefined
        ? undefined
        : [...menusEl.querySelectorAll<HTMLInputElement>('input')].find(
            (i) => i.dataset.fkey === focused.menu && i.dataset.fvalue === focused.value,
          );
    (tick ?? summaryOf((focusMenu ?? focused?.menu) as FilterKey | undefined))?.focus();
    focusMenu = null;
    replaceChildren(tagsEl, filterTags(inbox, toggle, clear));
    tagsEl.hidden = tagsEl.childElementCount === 0;
  };

  const update = () => {
    const ticks = FILTER_KEYS.map((key) => [...inbox.filters[key]].join('\u0001')).join('\u0002');
    if (painted && painted.all === inbox.all && painted.ticks === ticks) return;
    painted = { all: inbox.all, ticks };
    repaint();
  };

  // a click elsewhere or Escape, wherever the focus is, closes the open menu; one menu open at a time
  const onDocClick = (e: MouseEvent) => {
    const open = openMenu();
    if (open && !open.contains(e.target as Node)) open.open = false;
  };
  const onKey = (e: KeyboardEvent) => {
    const open = openMenu();
    if (e.key !== 'Escape' || !open) return;
    open.open = false;
    open.querySelector('summary')?.focus();
  };
  menusEl.addEventListener(
    'toggle',
    (e) => {
      const opened = e.target as HTMLDetailsElement;
      if (opened.open) for (const d of menus()) if (d !== opened) d.open = false;
    },
    true,
  );
  document.addEventListener('click', onDocClick);
  document.addEventListener('keydown', onKey);
  update();
  return {
    el,
    update,
    dispose: () => {
      document.removeEventListener('click', onDocClick);
      document.removeEventListener('keydown', onKey);
    },
  };
};
